import React from 'react';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faQuestion } from '@fortawesome/pro-solid-svg-icons/faQuestion';
import { Flex } from '@osmosis/react-ui/components/Box';
import { Box } from '@osmosis/react-ui/components/Box';
import { useColors } from '@osmosis/react-ui/hooks/useColors';
import { P } from '@osmosis/react-ui/components/Text';
import headingSchema from '../../../lib/composer/schemas/heading';
import subheadingSchema from '../../../lib/composer/schemas/subheading';
import themeSchema from '../../../lib/composer/schemas/theme';
import Heading from '../block-ui/Heading';
import Subheading from '../block-ui/Subheading';
import BlockWrapper from '../BlockWrapper';
import rowsSchema from '../../../lib/composer/schemas/guideGrid';
import {
  ComposerBlockElement,
  EditablePropsType,
} from '../../../types/composer';
import { GuideGridBlock } from '../../../types/plans';

/**
 * Return Tick component for Guide Grid table.
 */
const Tick = (props: { ticked: boolean }) => {
  const { ticked } = props;

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      backgroundColor={ticked ? 'black' : 'lightgrey'}
      color="white"
      fontSize={10}
      sx={{
        borderRadius: '50px',
        height: '18px',
        width: '18px',
      }}
    >
      <Icon
        icon={ticked ? faCheck : faQuestion}
        style={{
          fontSize: '15px',
        }}
      />
    </Flex>
  );
};

/**
 * Return Guide Grid component.
 */
const GuideGrid: ComposerBlockElement = (props: GuideGridBlock) => {
  const {
    heading,
    subheading,
    rows,
    theme = 'primary',
    anchor,
    columns,
    topHeader,
  } = props;

  // allPackages array rewritten so that it takes order into account

  const colors = useColors();

  return (
    <Flex id={anchor} bg={'bg-0'}>
      <BlockWrapper width="100%">
        <Flex justifyContent="center">
          <Flex width="100%">
            <Flex width="100%" flexDirection="column" justifyContent="center">
              <Flex
                flexDirection="column"
                sx={{ gap: 3 }}
                alignItems="center"
                mb="30px"
              >
                {heading && <Heading text={heading} topHeader={topHeader} />}
                {subheading && (
                  <Subheading text={subheading} topHeader={topHeader} />
                )}
              </Flex>
              <Box>
                <Flex
                  flexDirection="column"
                  bg={colors[theme] || 'bg-1'}
                  py={4}
                >
                  {/* GRID HERE */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${
                        (columns?.length ?? 0) + 1
                      }, minmax(0, 1fr))`,
                    }}
                  >
                    <Box />
                    {columns?.map((column) => (
                      <Box
                        key={column}
                        textAlign="center"
                        color="text"
                        fontSize={18}
                        fontWeight="bold"
                        p="5px"
                      >
                        {column}
                      </Box>
                    ))}
                    {rows?.map((row, index) => {
                      const border =
                        index === rows.length - 1
                          ? {}
                          : {
                              borderBottom: '1px solid',
                              borderColor: 'gray10',
                            };
                      return (
                        <React.Fragment key={index}>
                          <Flex
                            sx={{
                              gap: 4,
                              ...border,
                            }}
                            py={3}
                            alignItems="center"
                          >
                            <Box maxWidth={112} ml={4}>
                              <img
                                src={row.image.src}
                                alt={row.image.altText}
                                style={{
                                  width: '112px',
                                  height: 'auto',
                                }}
                              />
                            </Box>
                            <Box>
                              <P fontWeight={700}>{row.heading}</P>
                              <Box
                                fontSize={12}
                                dangerouslySetInnerHTML={{
                                  __html: row.subheading,
                                }}
                              />
                            </Box>
                          </Flex>
                          {columns?.map((column) => (
                            <Flex
                              key={[row.heading, column].join('-')}
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                ...border,
                              }}
                            >
                              {row.enabledColumns.includes(column) ? (
                                <Tick ticked={true} />
                              ) : (
                                <Tick ticked={false} />
                              )}
                            </Flex>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </BlockWrapper>
    </Flex>
  );
};

const columnsSchema = {
  label: 'Columns',
  name: 'columns',
  type: EditablePropsType.Array,
  items: {
    type: EditablePropsType.Text,
    name: 'column',
    label: 'Column',
    required: true,
  },
};

GuideGrid.schema = {
  name: 'package-guide',
  label: 'Guide Grid',
  editableProps: [
    headingSchema,
    subheadingSchema,
    columnsSchema,
    rowsSchema,
    themeSchema,
  ],
};

export default GuideGrid;
